/**
 * One entity, one node. The pre-rendered HTML already carries this page's JSON-LD, and the live
 * pages are saved after React has run, so anything React adds a second time ends up in the file
 * twice. Google merges nodes by @id, and two nodes with the same @id each carrying an
 * aggregateRating is what Search Console reports as "Review has multiple aggregate ratings".
 *
 * So: React never adds a node that is already in the document, and the pre-rendered nodes are
 * dropped the moment the visitor navigates away from the URL they were built for.
 */
const SELECTOR = 'script[type="application/ld+json"]';
const PRERENDERED = "script[data-pg-prerendered]";

const parse = (script) => {
  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return null;
  }
};

/** What makes two nodes the same entity: the @id where there is one, otherwise type and name. */
export const entityKey = (node) => {
  if (!node || typeof node !== "object") return null;
  if (node["@id"]) return String(node["@id"]);
  const type = [].concat(node["@type"] || []).join(",");
  return `${type}:${node.name || node.url || node.headline || ""}`;
};

const presentKeys = () => {
  const keys = new Set();
  document.querySelectorAll(SELECTOR).forEach((script) => {
    const key = entityKey(parse(script));
    if (key) keys.add(key);
  });
  return keys;
};

/** Remove the pre-rendered nodes, which describe the URL the browser first loaded. */
export const dropPrerenderedJsonLd = () => {
  document.querySelectorAll(PRERENDERED).forEach((script) => script.remove());
};

/**
 * Add the nodes that are not already described in the document, and return the scripts created so
 * the caller can remove them again.
 */
export const injectJsonLd = (nodes, marker) => {
  const keys = presentKeys();
  const created = [];
  (Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean).forEach((node) => {
    const key = entityKey(node);
    if (key && keys.has(key)) return;
    if (key) keys.add(key);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(marker, "1");
    script.textContent = JSON.stringify(node);
    document.head.appendChild(script);
    created.push(script);
  });
  return created;
};
